type Placement = 'top' | 'bottom' | 'left' | 'right';

const TOOLTIP_CLASS = 'fixed z-50 pointer-events-none transition-opacity duration-150';

const CARET_CLASSES: Record<Placement, string> = {
  top: 'absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-black/80',
  bottom:
    'absolute left-1/2 -translate-x-1/2 -top-1.5 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-black/80',
  left: 'absolute top-1/2 -translate-y-1/2 -right-1.5 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[6px] border-l-black/80',
  right:
    'absolute top-1/2 -translate-y-1/2 -left-1.5 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-black/80',
};

/**
 * Gets or creates a tooltip DOM element. Ensures only one element per ID exists.
 */
export function getOrCreateTooltipEl(id: string): HTMLDivElement {
  let el = document.getElementById(id) as HTMLDivElement | null;
  if (!el) {
    el = document.createElement('div');
    el.id = id;
    el.className = TOOLTIP_CLASS;
    document.body.appendChild(el);
  }
  return el;
}

/**
 * Removes a tooltip DOM element by ID. Call this on component unmount.
 */
export function removeTooltipEl(id: string): void {
  const el = document.getElementById(id);
  if (el) {
    el.remove();
  }
}

/**
 * Determines the best placement for a tooltip based on available viewport space.
 */
function determinePlacement(
  caretX: number,
  caretY: number,
  width: number,
  height: number,
  gap: number,
): Placement {
  const spaceTop = caretY - gap;
  const spaceBottom = window.innerHeight - caretY - gap;
  const spaceLeft = caretX - gap;
  const spaceRight = window.innerWidth - caretX - gap;

  if (spaceTop >= height) return 'top';
  if (spaceBottom >= height) return 'bottom';
  if (spaceRight >= width) return 'right';
  if (spaceLeft >= width) return 'left';
  return 'top';
}

/**
 * Positions the tooltip element relative to the caret point.
 * Handles placement, caret styling, and viewport clamping.
 */
export function positionTooltip(
  tooltipEl: HTMLElement,
  caretX: number,
  caretY: number,
  caretId: string,
): void {
  const caretSize = 6;
  const gap = 8;

  const actualWidth = tooltipEl.offsetWidth;
  const actualHeight = tooltipEl.offsetHeight;

  const placement = determinePlacement(caretX, caretY, actualWidth, actualHeight, gap);

  // Update caret classes
  const caretEl = tooltipEl.querySelector(`#${caretId}`);
  if (caretEl) {
    caretEl.className = CARET_CLASSES[placement];
  }

  let left = 0;
  let top = 0;

  if (placement === 'top') {
    left = caretX - actualWidth / 2;
    top = caretY - actualHeight - caretSize - 2;
  } else if (placement === 'bottom') {
    left = caretX - actualWidth / 2;
    top = caretY + caretSize + 2;
  } else if (placement === 'left') {
    left = caretX - actualWidth - caretSize - 2;
    top = caretY - actualHeight / 2;
  } else if (placement === 'right') {
    left = caretX + caretSize + 2;
    top = caretY - actualHeight / 2;
  }

  // Clamp within viewport
  left = Math.max(5, Math.min(left, window.innerWidth - actualWidth - 5));
  top = Math.max(5, Math.min(top, window.innerHeight - actualHeight - 5));

  tooltipEl.style.opacity = '1';
  tooltipEl.style.left = left + 'px';
  tooltipEl.style.top = top + 'px';
}

/**
 * Returns the max-width style value based on screen size.
 */
export function getTooltipMaxWidth(): string {
  return window.innerWidth < 640 ? 'calc(100vw - 20px)' : '260px';
}

/**
 * Builds a tooltip table row HTML string.
 */
export function tooltipRow(
  labelText: string,
  value: string,
  valueColor?: string,
  isBold = true,
): string {
  const fontClass = isBold ? 'font-medium' : '';
  const colorStyle = valueColor ? ` style="color: ${valueColor};"` : '';
  return `<tr>
    <td class="pr-4 py-0.5 text-gray-400">${labelText}</td>
    <td class="text-right py-0.5 ${fontClass}"${colorStyle}>${value}</td>
  </tr>`;
}

/**
 * Wraps tooltip table rows in the standard tooltip container.
 */
export function wrapTooltipTable(rows: string, caretId: string): string {
  const maxWidth = getTooltipMaxWidth();
  return `<div class="bg-black/80 rounded-md text-white text-xs py-1.5 px-2.5 relative" style="max-width: ${maxWidth};">
    <table class="w-full" style="border-collapse: collapse;">${rows}</table>
    <div id="${caretId}"></div>
  </div>`;
}
